import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useLadder } from '../contexts/LadderContext';
import { ArrowLeft, Users, Target, Swords, TrendingUp, ChevronDown, ChevronUp, Calendar, Gauge, Flame, Trophy, Medal } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import { LevelCertaintyInfo, formatLevel, getCertaintyLabel } from '../components/LevelCertainty';

// Rank badge component with medal colors for top 3
const RankBadge = ({ rank }) => {
  if (!rank || rank === '?') return null;
  
  const rankNum = parseInt(rank);
  
  // Base classes for circular badge
  const baseClasses = "inline-flex items-center justify-center w-7 h-7 min-w-7 min-h-7 aspect-square rounded-full font-bold text-sm shadow-sm";
  
  // Medal colors for top 3
  if (rankNum === 1) {
    return (
      <span className={`${baseClasses} bg-gradient-to-br from-yellow-300 to-yellow-500 text-yellow-900`}>
        {rank}
      </span>
    );
  }
  if (rankNum === 2) {
    return (
      <span className={`${baseClasses} bg-gradient-to-br from-slate-200 to-slate-400 text-slate-700`}>
        {rank}
      </span>
    );
  }
  if (rankNum === 3) {
    return (
      <span className={`${baseClasses} bg-gradient-to-br from-amber-500 to-amber-700 text-amber-100`}>
        {rank}
      </span>
    );
  }
  
  // Regular rank badge for 4+ - white background for contrast
  return (
    <span className={`${baseClasses} bg-white dark:bg-slate-700 text-foreground border border-border`}>
      {rank}
    </span>
  );
};

// Generate player slug: firstname + first letter(s) of surname
export const generatePlayerSlug = (name, allPlayers = []) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) {
    return name.toLowerCase().replace(/\s+/g, '-');
  }
  
  const firstName = parts[0].toLowerCase();
  const surname = parts.slice(1).join(' ');
  
  // Try increasingly longer surname prefixes
  for (let len = 1; len <= surname.length; len++) {
    const slug = `${firstName}-${surname.substring(0, len).toLowerCase()}`;
    const isDuplicate = allPlayers.some(p => {
      if (p.name === name) return false;
      const otherSlug = generatePlayerSlugSimple(p.name, len);
      return otherSlug === slug;
    });
    if (!isDuplicate) return slug;
  }
  
  // If still duplicate, append number
  let counter = 2;
  const baseSlug = `${firstName}-${surname.toLowerCase()}`;
  let slug = baseSlug;
  while (allPlayers.some(p => p.name !== name && generatePlayerSlugSimple(p.name, surname.length) === slug)) {
    slug = `${baseSlug}-${counter}`;
    counter++;
  }
  return slug;
};

const generatePlayerSlugSimple = (name, surnameLen = 1) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name.toLowerCase().replace(/\s+/g, '-');
  const firstName = parts[0].toLowerCase();
  const surname = parts.slice(1).join(' ');
  return `${firstName}-${surname.substring(0, surnameLen).toLowerCase()}`;
};

// Score card component (same as dashboard)
const MatchScoreCard = ({ match, getPlayerById, currentPlayerId, language }) => {
  const teamAP1 = getPlayerById(match.team_a_player1_id);
  const teamAP2 = getPlayerById(match.team_a_player2_id);
  const teamBP1 = getPlayerById(match.team_b_player1_id);
  const teamBP2 = getPlayerById(match.team_b_player2_id);
  const isTeamAWinner = match.winner_team === 'A';
  const sets = match.sets || [];
  const maxSets = 3;

  // Determine which team the current player is on
  const isPlayerTeamA = [match.team_a_player1_id, match.team_a_player2_id].includes(currentPlayerId);
  const playerWon = (isPlayerTeamA && isTeamAWinner) || (!isPlayerTeamA && !isTeamAWinner);

  // Format date
  const dateLabel = match.date ? format(new Date(match.date), 'd MMM', { locale: language === 'el' ? el : enUS }) : '';

  return (
    <div className={`bg-card rounded-lg border p-3 ${playerWon ? 'border-green-200 dark:border-green-800/50' : 'border-border'}`}>
      {dateLabel && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2 pb-2 border-b border-border">
          <Calendar className="h-3 w-3" />
          <span>{dateLabel}</span>
        </div>
      )}
      <div className="space-y-2">
        <div className={`flex items-center justify-between ${isTeamAWinner ? 'text-foreground' : 'text-muted-foreground'}`}>
          <div className={`flex items-center gap-2 min-w-0 flex-1 ${isTeamAWinner ? 'font-medium' : ''}`}>
            {isTeamAWinner && <div className="w-0.5 h-4 bg-green-500 rounded-full flex-shrink-0" />}
            <span className="text-sm truncate">
              {teamAP1?.name?.split(' ')[0]} / {teamAP2?.name?.split(' ')[0]}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            {Array.from({ length: maxSets }).map((_, idx) => (
              <span 
                key={`set-a-${idx}`} 
                className={`text-xs font-mono w-4 text-center ${
                  sets[idx] 
                    ? sets[idx].team_a > sets[idx].team_b 
                      ? 'text-green-600 dark:text-green-400 font-semibold' 
                      : ''
                    : 'invisible'
                }`}
              >
                {sets[idx]?.team_a ?? '-'}
              </span>
            ))}
          </div>
        </div>
        <div className={`flex items-center justify-between ${!isTeamAWinner ? 'text-foreground' : 'text-muted-foreground'}`}>
          <div className={`flex items-center gap-2 min-w-0 flex-1 ${!isTeamAWinner ? 'font-medium' : ''}`}>
            {!isTeamAWinner && <div className="w-0.5 h-4 bg-green-500 rounded-full flex-shrink-0" />}
            <span className="text-sm truncate">
              {teamBP1?.name?.split(' ')[0]} / {teamBP2?.name?.split(' ')[0]}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            {Array.from({ length: maxSets }).map((_, idx) => (
              <span 
                key={`set-b-${idx}`} 
                className={`text-xs font-mono w-4 text-center ${
                  sets[idx] 
                    ? sets[idx].team_b > sets[idx].team_a 
                      ? 'text-green-600 dark:text-green-400 font-semibold' 
                      : ''
                    : 'invisible'
                }`}
              >
                {sets[idx]?.team_b ?? '-'}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function PlayerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { players, matches, leaderboard, ladder, loading, getPlayerById } = useLadder();
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'career'

  // Find the player
  const player = useMemo(() => {
    return players.find(p => p.id === id);
  }, [players, id]);

  // Calculate player stats
  const stats = useMemo(() => {
    if (!player) return null;

    // Get all matches for this player
    const allPlayerMatches = matches.filter(m => 
      [m.team_a_player1_id, m.team_a_player2_id, m.team_b_player1_id, m.team_b_player2_id].includes(player.id)
    );

    // Filter matches by current period dates
    const periodStart = ladder?.period_start_date ? new Date(ladder.period_start_date) : null;
    const periodEnd = ladder?.period_end_date ? new Date(ladder.period_end_date) : null;
    
    // If period is configured, filter matches by date range
    let playerMatches = allPlayerMatches;
    if (periodStart && periodEnd) {
      // Set end date to end of day
      periodEnd.setHours(23, 59, 59, 999);
      playerMatches = allPlayerMatches.filter(m => {
        const matchDate = m.date ? new Date(m.date) : new Date(m.created_at);
        return matchDate >= periodStart && matchDate <= periodEnd;
      });
    } else {
      // Period not configured - show no matches for current period (clean slate)
      playerMatches = [];
    }

    // If no matches in current period, return default stats
    if (playerMatches.length === 0) {
      return {
        matchesPlayed: 0,
        wins: 0,
        losses: 0,
        winRate: 0,
        uniquePartners: 0,
        totalPossiblePartners: Math.max(0, players.length - 1),
        variety: 0,
        rank: null,
        rankChange: 0,
        streak: null,
        recentMatches: [],
        allMatches: allPlayerMatches, // Keep all matches for history view
        recentForm: [],
        bestPartnership: null,
        stuckPartnership: null,
        rivalry: null
      };
    }

    // Sort by date descending
    const sortedMatches = [...playerMatches].sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(a.created_at);
      const dateB = b.date ? new Date(b.date) : new Date(b.created_at);
      return dateB - dateA;
    });

    let wins = 0;
    let losses = 0;
    const partnerCounts = {};
    const opponentCounts = {};
    const partnerWins = {};
    const opponentRecord = {};

    playerMatches.forEach(match => {
      const isTeamA = [match.team_a_player1_id, match.team_a_player2_id].includes(player.id);
      const isWinner = (isTeamA && match.winner_team === 'A') || (!isTeamA && match.winner_team === 'B');
      
      if (isWinner) wins++;
      else losses++;

      // Track partners
      let partnerId;
      if (isTeamA) {
        partnerId = match.team_a_player1_id === player.id ? match.team_a_player2_id : match.team_a_player1_id;
      } else {
        partnerId = match.team_b_player1_id === player.id ? match.team_b_player2_id : match.team_b_player1_id;
      }
      partnerCounts[partnerId] = (partnerCounts[partnerId] || 0) + 1;
      if (!partnerWins[partnerId]) partnerWins[partnerId] = { wins: 0, total: 0 };
      partnerWins[partnerId].total++;
      if (isWinner) partnerWins[partnerId].wins++;

      // Track opponents
      const opponents = isTeamA 
        ? [match.team_b_player1_id, match.team_b_player2_id]
        : [match.team_a_player1_id, match.team_a_player2_id];
      
      opponents.forEach(oppId => {
        opponentCounts[oppId] = (opponentCounts[oppId] || 0) + 1;
        if (!opponentRecord[oppId]) opponentRecord[oppId] = { wins: 0, losses: 0 };
        if (isWinner) opponentRecord[oppId].wins++;
        else opponentRecord[oppId].losses++;
      });
    });

    const uniquePartners = Object.keys(partnerCounts).length;
    const totalPossiblePartners = players.length - 1;
    // Variety = unique partners / total matches played (e.g., 3 partners in 3 matches = 100%)
    const variety = playerMatches.length > 0 ? Math.round((uniquePartners / playerMatches.length) * 100) : 0;

    // Best partnership (2+ matches)
    let bestPartnership = null;
    Object.entries(partnerWins).forEach(([partnerId, record]) => {
      if (record.total >= 2) {
        const winPct = Math.round((record.wins / record.total) * 100);
        if (!bestPartnership || winPct > bestPartnership.winPct) {
          const partnerPlayer = getPlayerById(partnerId);
          bestPartnership = {
            partnerId,
            partnerName: partnerPlayer?.name?.split(' ')[0] || 'Unknown',
            winPct,
            matches: record.total
          };
        }
      }
    });

    // Stuck Partnership (3+ matches with same partner)
    let stuckPartnership = null;
    Object.entries(partnerCounts).forEach(([partnerId, count]) => {
      if (count >= 3) {
        if (!stuckPartnership || count > stuckPartnership.count) {
          const partnerPlayer = getPlayerById(partnerId);
          stuckPartnership = {
            partnerId,
            partnerName: partnerPlayer?.name?.split(' ')[0] || 'Unknown',
            count,
            totalMatches: playerMatches.length
          };
        }
      }
    });

    // Rivalry (3+ matches against same opponent)
    let rivalry = null;
    Object.entries(opponentRecord).forEach(([oppId, record]) => {
      const totalVs = record.wins + record.losses;
      if (totalVs >= 3) {
        if (!rivalry || totalVs > (rivalry.wins + rivalry.losses)) {
          const oppPlayer = getPlayerById(oppId);
          rivalry = {
            opponentId: oppId,
            opponentName: oppPlayer?.name?.split(' ')[0] || 'Unknown',
            wins: record.wins,
            losses: record.losses
          };
        }
      }
    });

    // Find rank - handle both array and object leaderboard formats
    const qualifiedPlayers = Array.isArray(leaderboard) ? leaderboard : (leaderboard?.qualified || []);
    const rankEntry = qualifiedPlayers.find(l => l.player_id === player.id);
    const rank = rankEntry?.rank || '?';

    // Calculate recent form (last 5 matches, W/L)
    const recentForm = sortedMatches.slice(0, 5).map(match => {
      const isTeamA = [match.team_a_player1_id, match.team_a_player2_id].includes(player.id);
      const isWinner = (isTeamA && match.winner_team === 'A') || (!isTeamA && match.winner_team === 'B');
      return isWinner ? 'W' : 'L';
    });

    // Calculate current win streak (from most recent matches)
    let winStreak = 0;
    for (const result of recentForm) {
      if (result === 'W') winStreak++;
      else break;
    }

    return {
      matchesPlayed: playerMatches.length,
      wins,
      losses,
      winRate: playerMatches.length > 0 ? Math.round((wins / playerMatches.length) * 100) : 0,
      uniquePartners,
      totalPossiblePartners,
      variety,
      stuckPartnership,
      rivalry,
      rank,
      recentForm,
      winStreak,
      recentMatches: sortedMatches.slice(0, 5),
      allMatches: allPlayerMatches.sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(a.created_at);
        const dateB = b.date ? new Date(b.date) : new Date(b.created_at);
        return dateB - dateA;
      })
    };
  }, [player, players, matches, leaderboard, ladder, getPlayerById]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!player) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 text-center">
        <p className="text-muted-foreground">Player not found</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('backToList')}
        </Button>
      </div>
    );
  }

  // Calculate matches needed to qualify (3 matches required)
  const matchesNeeded = stats ? Math.max(0, 3 - stats.matchesPlayed) : 3;
  const isRanked = stats?.rank && stats?.rank !== '?';

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="player-profile">
      {/* Back button */}
      <button 
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors mb-4"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm">{t('backToList')}</span>
      </button>

      {/* Header Section - using grid to align with card content */}
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-4">
          {/* Left column content area */}
          <div className="pl-4">
            {(() => {
              const nameParts = player.name.split(' ');
              const firstName = nameParts[0];
              const lastName = nameParts.slice(1).join(' ');
              const isDefendingChampion = ladder?.defending_champion_id === player.id;
              
              return (
                <>
                  {/* First line: First name + champion badge + rank badge */}
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="font-heading text-2xl font-bold text-foreground">{firstName}</h1>
                    {isDefendingChampion && (
                      <span className="text-amber-500 font-bold text-lg" title={t('defendingChampion')}>Ⓒ</span>
                    )}
                    {isRanked && <RankBadge rank={stats?.rank} />}
                  </div>
                  
                  {/* Second line: Last name */}
                  {lastName && (
                    <span className="text-lg text-muted-foreground">{lastName}</span>
                  )}
                </>
              );
            })()}
          </div>
          {/* Right column - empty for alignment */}
          <div />
        </div>
        
        {/* Horizontal divider */}
        <div className="border-t border-border my-4" />
        
        {/* Stats row: Level/Certainty (left) | Recent Form (right) - shown for ALL players */}
        <div className="grid grid-cols-2 gap-4">
          {/* Left: Level: [Level] · Certainty (i) - aligned with card content */}
          <div className="pl-4 flex items-center gap-1.5 text-muted-foreground">
            {player.mu != null ? (
              <>
                <span className="text-muted-foreground">Level:</span>
                <span className="font-semibold text-foreground tabular-nums">{formatLevel(player.mu)}</span>
                <span className="text-muted-foreground/60">·</span>
                <span>
                  {(() => {
                    const cert = getCertaintyLabel(player.sigma, true);
                    return cert?.main === 'New' ? 'New' : cert?.main?.replace(' Certainty', '') || '—';
                  })()}
                </span>
                <LevelCertaintyInfo language={language} />
              </>
            ) : (
              <span className="text-muted-foreground text-sm">{language === 'el' ? 'Χωρίς level ακόμα' : 'No level yet'}</span>
            )}
          </div>
          
          {/* Right: Recent form + fire icon - aligned with card content, left-aligned */}
          {/* Order: oldest match first (left) → most recent match last (right) */}
          <div className="pl-4 flex items-center gap-1.5">
            {(stats?.recentForm?.length > 0) ? (
              <>
                {[...(stats?.recentForm || [])].reverse().map((result, idx) => (
                  <span 
                    key={`form-${idx}`}
                    className={`text-sm font-bold ${
                      result === 'W' 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-500 dark:text-red-400'
                    }`}
                  >
                    {result}
                  </span>
                ))}
                {stats?.winStreak >= 3 && (
                  <Flame className="h-4 w-4 text-orange-500 ml-1" />
                )}
              </>
            ) : (
              <span className="text-muted-foreground text-sm">{language === 'el' ? 'Χωρίς αγώνες' : 'No matches'}</span>
            )}
          </div>
        </div>
      </div>

      {/* Player Stats - Show for ALL players (ranked and unranked) */}
      <Tabs defaultValue="current" className="mb-6">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="current" data-testid="tab-current-period">
            {t('currentPeriod')}
          </TabsTrigger>
          <TabsTrigger value="career" data-testid="tab-career">
            {t('career')}
          </TabsTrigger>
        </TabsList>
        
        {/* Current Period Tab */}
        <TabsContent value="current">
          {/* Unranked notice */}
          {!isRanked && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800/30 p-3 mb-4">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                <span className="font-medium">{language === 'el' ? 'Χωρίς κατάταξη' : 'Unranked'}</span>
                {matchesNeeded > 0 && (
                  <span className="ml-1">
                    — {language === 'el' 
                      ? `${matchesNeeded} ακόμα αγώνες για κατάταξη` 
                      : `${matchesNeeded} more match${matchesNeeded > 1 ? 'es' : ''} to qualify`}
                  </span>
                )}
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
              {/* Column 1: Relationships - Compact layout */}
              <div className="bg-card rounded-xl border border-border p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-3">
                  <Users className="h-4 w-4" />
                  <span className={`text-xs font-medium tracking-wider ${language !== 'el' ? 'uppercase' : ''}`}>
                    {t('relationships')}
                  </span>
                </div>
                
                {/* Horizontal layout: Partners | Variety */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{t('uniquePartnersStat')}</p>
                    <p className="text-lg font-bold text-foreground tabular-nums">
                      {stats?.uniquePartners}
                      <span className="text-sm font-normal text-muted-foreground/60">/{stats?.totalPossiblePartners}</span>
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{t('variety')}</p>
                    <p className="text-lg font-bold text-foreground tabular-nums">
                      {stats?.variety}%
                    </p>
                    <p className="text-xs text-muted-foreground/60 tabular-nums">
                      ({stats?.uniquePartners}/{stats?.matchesPlayed})
                    </p>
                  </div>
                </div>

                {/* Divider + Stuck/Rivalry */}
                <div className="border-t border-border pt-3">
                  {stats?.stuckPartnership ? (
                    <div>
                      <p className="text-xs text-muted-foreground">{t('stuckPartnership')}</p>
                      <p className="text-sm text-muted-foreground">
                        {stats.stuckPartnership.count} {t('outOfGames').replace('{0}', stats.stuckPartnership.totalMatches)} {stats.stuckPartnership.partnerName}
                      </p>
                    </div>
                  ) : stats?.rivalry ? (
                    <div>
                      <p className="text-xs text-muted-foreground">{t('rivalry')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('vsOpponent')} {stats.rivalry.opponentName}{' '}
                        <span className="text-foreground font-medium">
                          {stats.rivalry.wins}–{stats.rivalry.losses}
                        </span>
                      </p>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground opacity-50">—</div>
                  )}
                </div>
              </div>

              {/* Column 2: Performance */}
              <div className="bg-card rounded-xl border border-border p-4 flex flex-col">
                <div className="flex items-center gap-2 text-muted-foreground mb-3">
                  <Target className="h-4 w-4" />
                  <span className={`text-xs font-medium tracking-wider ${language !== 'el' ? 'uppercase' : ''}`}>
                    {t('performance')}
                  </span>
                </div>
                
                {/* Matches / Wins / Losses in a row */}
                <div className="grid grid-cols-3 gap-2 text-center mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('matchesPlayedStat')}</p>
                    <p className="text-lg font-bold text-foreground tabular-nums">{stats?.matchesPlayed}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('winsStat')}</p>
                    <p className="text-lg font-bold text-green-600 dark:text-green-400 tabular-nums">{stats?.wins}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('lossesStat')}</p>
                    <p className="text-lg font-bold text-red-600 dark:text-red-400 tabular-nums">{stats?.losses}</p>
                  </div>
                </div>

                {/* Win Rate with bar */}
                <div className="border-t border-border pt-3 mt-auto">
                  <p className="text-xs text-muted-foreground mb-1">{t('winRate')}</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 dark:bg-green-400 rounded-full transition-all"
                        style={{ width: `${stats?.winRate || 0}%` }}
                      />
                    </div>
                    <p className="text-sm font-medium text-foreground tabular-nums w-10 text-right">{stats?.winRate}%</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Career Tab */}
          <TabsContent value="career">
            <div className="grid grid-cols-2 gap-4">
              {/* Column 1: Achievements */}
              <div className="bg-card rounded-xl border border-border p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-3">
                  <Trophy className="h-4 w-4" />
                  <span className={`text-xs font-medium tracking-wider ${language !== 'el' ? 'uppercase' : ''}`}>
                    {t('achievements')}
                  </span>
                </div>
                
                <div className="space-y-3">
                  {/* Titles - inline layout */}
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30">
                      <Trophy className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <span className="text-lg font-bold text-foreground tabular-nums">{player.titles || 0}</span>
                    <span className="text-xs text-muted-foreground">{t('titles')}</span>
                  </div>
                  
                  {/* Runner-Ups - inline layout */}
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-md bg-slate-100 dark:bg-slate-800">
                      <Medal className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                    </div>
                    <span className="text-lg font-bold text-foreground tabular-nums">{player.runner_ups || 0}</span>
                    <span className="text-xs text-muted-foreground">{t('runnerUps')}</span>
                  </div>
                </div>
              </div>

              {/* Column 2: Career Stats (Previous + Current Period) */}
              <div className="bg-card rounded-xl border border-border p-4 flex flex-col">
                <div className="flex items-center gap-2 text-muted-foreground mb-3">
                  <Target className="h-4 w-4" />
                  <span className={`text-xs font-medium tracking-wider ${language !== 'el' ? 'uppercase' : ''}`}>
                    {t('careerStats')}
                  </span>
                </div>
                
                {/* Career Matches / Wins / Losses - directly from career stats (already includes all matches) */}
                {(() => {
                  const totalMatches = player.career_matches || 0;
                  const totalWins = player.career_wins || 0;
                  const totalLosses = player.career_losses || 0;
                  const totalWinRate = totalMatches > 0 ? Math.round((totalWins / totalMatches) * 100) : 0;
                  
                  return (
                    <>
                      <div className="grid grid-cols-3 gap-2 text-center mb-3">
                        <div>
                          <p className="text-xs text-muted-foreground">{t('matchesPlayedStat')}</p>
                          <p className="text-lg font-bold text-foreground tabular-nums">{totalMatches}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('winsStat')}</p>
                          <p className="text-lg font-bold text-green-600 dark:text-green-400 tabular-nums">{totalWins}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('lossesStat')}</p>
                          <p className="text-lg font-bold text-red-600 dark:text-red-400 tabular-nums">{totalLosses}</p>
                        </div>
                      </div>

                      {/* Career Win Rate with bar */}
                      <div className="border-t border-border pt-3 mt-auto">
                        <p className="text-xs text-muted-foreground mb-1">{t('winRate')}</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500 dark:bg-green-400 rounded-full transition-all"
                              style={{ width: `${totalWinRate}%` }}
                            />
                          </div>
                          <p className="text-sm font-medium text-foreground tabular-nums w-10 text-right">{totalWinRate}%</p>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </TabsContent>
        </Tabs>

      {/* Latest Matches - always shown */}
      <div className="bg-card rounded-xl border border-border p-4">
        <h2 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
          <Swords className="h-4 w-4" />
          {t('latestMatches')}
        </h2>

        {stats?.recentMatches?.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">{t('noMatches')}</p>
        ) : (
          <div className="space-y-3">
            {(showFullHistory ? stats?.allMatches : stats?.recentMatches)?.map(match => (
              <MatchScoreCard
                key={match.id}
                match={match}
                getPlayerById={getPlayerById}
                currentPlayerId={player.id}
                language={language}
              />
            ))}
          </div>
        )}

        {stats?.allMatches?.length > 5 && (
          <button
            onClick={() => setShowFullHistory(!showFullHistory)}
            className="w-full mt-4 py-2 text-sm text-primary hover:text-primary/80 flex items-center justify-center gap-1 transition-colors"
            data-testid="toggle-history"
          >
            {showFullHistory ? (
              <>
                {t('hideFullHistory')} <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                {t('showFullHistory')} <ChevronDown className="h-4 w-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
